import React, { useState, useEffect } from "react";
import {
  Box, Paper, Typography, Button, Grid, Card, CardContent,
  FormControl, InputLabel, Select, MenuItem, Alert, Snackbar,
  Divider, Switch, FormControlLabel, TextField, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Tabs, Tab, Autocomplete
} from "@mui/material";
import {
  Settings, AccountBalance, Save, Refresh, CheckCircle, Warning,
  Add, Edit, Delete, Business, AccountTree
} from "@mui/icons-material";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function ConfiguracionContable() {
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [tabActiva, setTabActiva] = useState(0);

  // Estado unificado de asignación de cuentas por proceso
  const [asignacion, setAsignacion] = useState({
    // Ventas
    cuenta_ingresos: "",
    cuenta_iva_ventas: "",
    cuenta_iva_ventas_0: "",
    cuenta_clientes: "",
    // Compras
    cuenta_inventario: "",
    cuenta_gastos: "",
    cuenta_iva_compras: "",
    cuenta_proveedores: "",
    // Bancos / Caja
    cuenta_bancos: "",
    cuenta_caja: "",
    // Retenciones
    cuenta_retencion_fuente: "",
    cuenta_retencion_iva: "",
    // Costos
    cuenta_costos: "",
    // Liquidación de compras
    cuenta_liquidacion_compras: "",
    // Margen de utilidad por defecto (%)
    porcentaje_utilidad: 30,
    // Módulos habilitados
    retenciones_habilitadas: false,
    centro_costos_habilitado: false,
    tesoreria_habilitada: false,
    pos_habilitado: false,
    arqueo_caja_habilitado: false,
    recibos_sin_factura_habilitado: false,
    cuenta_recibos_pendientes: '', // Cuenta contable donde van los recibos hasta facturar
    permitir_venta_sin_stock: true,
  });

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  // Buscar cuenta por código (soporta con y sin puntos)
  const buscarCuenta = (listaCuentas, ...codigos) => {
    for (const codigo of codigos) {
      const found = listaCuentas.find(c => c.codigo === codigo);
      if (found) return found.id;
    }
    return "";
  };

  // Asignar cuentas por defecto basándose en el plan de cuentas NIIF Ecuador
  const asignarCuentasPorDefecto = (listaCuentas) => {
    setAsignacion({
      cuenta_ingresos: buscarCuenta(listaCuentas, "4010101"),
      cuenta_iva_ventas: buscarCuenta(listaCuentas, "2010501"),
      cuenta_iva_ventas_0: buscarCuenta(listaCuentas, "4010102"),
      cuenta_clientes: buscarCuenta(listaCuentas, "1010201"),
      cuenta_inventario: buscarCuenta(listaCuentas, "1010301"),
      cuenta_gastos: buscarCuenta(listaCuentas, "50214", "50207"),
      cuenta_iva_compras: buscarCuenta(listaCuentas, "1010401"),
      cuenta_proveedores: buscarCuenta(listaCuentas, "2010101"),
      cuenta_bancos: buscarCuenta(listaCuentas, "1010103"),
      cuenta_caja: buscarCuenta(listaCuentas, "1010101"),
      cuenta_retencion_fuente: buscarCuenta(listaCuentas, "2010502"),
      cuenta_retencion_iva: buscarCuenta(listaCuentas, "2010503"),
      cuenta_costos: buscarCuenta(listaCuentas, "50101"),
      cuenta_liquidacion_compras: buscarCuenta(listaCuentas, "50102"),
    });
  };

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const cuentasRes = await axios.get(`${API_URL}/api/contabilidad/plan-cuentas`, { headers: headers() });
      setCuentas(cuentasRes.data);

      try {
        const configRes = await axios.get(`${API_URL}/api/configuracion/contable`, { headers: headers() });
        const data = configRes.data;
        setAsignacion({
          cuenta_ingresos: data.cuenta_ingresos_general || "",
          cuenta_iva_ventas: data.cuenta_iva_ventas || "",
          cuenta_iva_ventas_0: data.cuenta_iva_ventas_0 || "",
          cuenta_clientes: data.cuenta_clientes_general || "",
          cuenta_inventario: data.cuenta_inventario_general || "",
          cuenta_gastos: data.cuenta_gastos_general || "",
          cuenta_iva_compras: data.cuenta_iva_compras || "",
          cuenta_proveedores: data.cuenta_proveedores_general || "",
          cuenta_bancos: data.cuenta_bancos_general || "",
          cuenta_caja: data.cuenta_caja_general || "",
          cuenta_retencion_fuente: data.cuenta_retencion_fuente || "",
          cuenta_retencion_iva: data.cuenta_retencion_iva || "",
          cuenta_costos: data.cuenta_costos_general || "",
          cuenta_liquidacion_compras: data.cuenta_liquidacion_compras || "",
          porcentaje_utilidad: data.porcentaje_utilidad || 30,
          retenciones_habilitadas: data.retenciones_habilitadas || false,
          centro_costos_habilitado: data.centro_costos_habilitado || false,
          tesoreria_habilitada: data.tesoreria_habilitada || false,
          pos_habilitado: data.pos_habilitado || false,
          arqueo_caja_habilitado: data.arqueo_caja_habilitado || false,
          recibos_sin_factura_habilitado: data.recibos_sin_factura_habilitado || false,
          cuenta_recibos_pendientes: data.cuenta_recibos_pendientes || '',
          permitir_venta_sin_stock: data.permitir_venta_sin_stock !== false, // true por defecto
        });
      } catch {
        // No hay configuración previa — asignar cuentas por defecto del plan
        asignarCuentasPorDefecto(cuentasRes.data);
      }
    } catch (error) {
      showSnackbar("Error al cargar datos", "error");
    } finally {
      setLoading(false);
    }
  };

  const guardarAsignacion = async () => {
    setSaving(true);
    try {
      // Guardar en formato compatible con el backend existente
      const payload = {
        cuenta_ingresos_general: asignacion.cuenta_ingresos,
        cuenta_iva_ventas: asignacion.cuenta_iva_ventas,
        cuenta_iva_ventas_0: asignacion.cuenta_iva_ventas_0,
        cuenta_clientes_general: asignacion.cuenta_clientes,
        cuenta_inventario_general: asignacion.cuenta_inventario,
        cuenta_gastos_general: asignacion.cuenta_gastos,
        cuenta_iva_compras: asignacion.cuenta_iva_compras,
        cuenta_proveedores_general: asignacion.cuenta_proveedores,
        cuenta_bancos_general: asignacion.cuenta_bancos,
        cuenta_caja_general: asignacion.cuenta_caja,
        cuenta_retencion_fuente: asignacion.cuenta_retencion_fuente,
        cuenta_retencion_iva: asignacion.cuenta_retencion_iva,
        cuenta_costos_general: asignacion.cuenta_costos,
        cuenta_liquidacion_compras: asignacion.cuenta_liquidacion_compras,
        porcentaje_utilidad: asignacion.porcentaje_utilidad,
        retenciones_habilitadas: asignacion.retenciones_habilitadas,
        centro_costos_habilitado: asignacion.centro_costos_habilitado,
        tesoreria_habilitada: asignacion.tesoreria_habilitada,
        pos_habilitado: asignacion.pos_habilitado,
        arqueo_caja_habilitado: asignacion.arqueo_caja_habilitado,
        recibos_sin_factura_habilitado: asignacion.recibos_sin_factura_habilitado,
        cuenta_recibos_pendientes: asignacion.cuenta_recibos_pendientes,
        permitir_venta_sin_stock: asignacion.permitir_venta_sin_stock,
      };
      await axios.post(`${API_URL}/api/configuracion/contable`, payload, { headers: headers() });
      showSnackbar("Asignación de cuentas guardada correctamente", "success");
      // Recargar datos para confirmar persistencia
      await cargarDatos();
    } catch (error) {
      showSnackbar(error.response?.data?.error || "Error al guardar", "error");
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAsignacionChange = (campo, valor) => {
    setAsignacion(prev => ({ ...prev, [campo]: valor }));
  };

  // Cuentas disponibles para selectores — usar TODAS las cuentas
  const opcionesCuentas = [...cuentas].sort((a, b) =>
    a.codigo.localeCompare(b.codigo, undefined, { numeric: true })
  );

  const renderSelectorCuenta = (campo, label, descripcion) => {
    return (
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel shrink>{label}</InputLabel>
          <Select
            value={asignacion[campo] || ""}
            label={label}
            displayEmpty
            notched
            onChange={(e) => handleAsignacionChange(campo, e.target.value)}
            MenuProps={{ PaperProps: { sx: { maxHeight: 350 } } }}
            renderValue={(selected) => {
              if (!selected) return <span style={{ color: "#999" }}>— Sin asignar —</span>;
              const c = cuentas.find(x => x.id === selected);
              return c ? `${c.codigo} - ${c.nombre}` : "Cuenta no encontrada";
            }}
          >
            <MenuItem value="">
              <em>— Sin asignar —</em>
            </MenuItem>
            {opcionesCuentas.map(cuenta => (
              <MenuItem key={cuenta.id} value={cuenta.id}>
                {cuenta.codigo} - {cuenta.nombre}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {descripcion}
          </Typography>
        </FormControl>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <Typography>Cargando configuración...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <Settings color="primary" />
          <Typography variant="h5" fontWeight={700}>
            Configuración Contable
          </Typography>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs value={tabActiva} onChange={(e, v) => setTabActiva(v)}>
            <Tab label="Plan de Cuentas" icon={<AccountTree />} iconPosition="start" />
            <Tab label="Asignación de Cuentas" icon={<AccountBalance />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Tab 0: Plan de Cuentas */}
        <TabPanel value={tabActiva} index={0}>
          <PlanCuentasIntegrado
            cuentas={cuentas}
            onCuentasChange={setCuentas}
            showSnackbar={showSnackbar}
          />
        </TabPanel>

        {/* Tab 1: Asignación de Cuentas por Proceso */}
        <TabPanel value={tabActiva} index={1}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Asigne las cuentas del plan contable a cada proceso del sistema. Estas cuentas se usarán
            automáticamente al generar asientos en facturas, compras y liquidaciones.
          </Alert>

          {/* Ventas */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                🧾 Ventas (Facturas emitidas)
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {renderSelectorCuenta("cuenta_ingresos", "Cuenta de Ingresos", "Ingresos por ventas de bienes/servicios")}
                {renderSelectorCuenta("cuenta_iva_ventas", "IVA Ventas 15%", "IVA cobrado en ventas tarifa 15%")}
                {renderSelectorCuenta("cuenta_iva_ventas_0", "IVA Ventas 0%", "Ventas con tarifa 0% de IVA")}
                {renderSelectorCuenta("cuenta_clientes", "Cuentas por Cobrar (Clientes)", "Clientes por cobrar")}
                {renderSelectorCuenta("cuenta_costos", "Costo de Ventas", "Costo de mercadería vendida")}
              </Grid>
            </CardContent>
          </Card>

          {/* Compras */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                🛒 Compras (Facturas recibidas)
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {renderSelectorCuenta("cuenta_inventario", "Cuenta de Inventario", "Inventario de mercadería / productos")}
                {renderSelectorCuenta("cuenta_gastos", "Cuenta de Gastos", "Gastos generales (servicios, suministros)")}
                {renderSelectorCuenta("cuenta_iva_compras", "IVA Compras (Pagado)", "IVA pagado en compras")}
                {renderSelectorCuenta("cuenta_proveedores", "Cuentas por Pagar (Proveedores)", "Proveedores por pagar")}
              </Grid>
            </CardContent>
          </Card>

          {/* Bancos / Caja */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                🏦 Bancos y Caja
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {renderSelectorCuenta("cuenta_bancos", "Cuenta de Bancos", "Cuenta bancaria principal")}
                {renderSelectorCuenta("cuenta_caja", "Cuenta de Caja", "Caja general / efectivo")}
              </Grid>
            </CardContent>
          </Card>

          {/* Retenciones */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                📋 Retenciones
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {renderSelectorCuenta("cuenta_retencion_fuente", "Retención en la Fuente", "Retención IR por pagar")}
                {renderSelectorCuenta("cuenta_retencion_iva", "Retención de IVA", "Retención IVA por pagar")}
              </Grid>
            </CardContent>
          </Card>

          {/* Liquidación de Compras */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                📄 Liquidación de Compras
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {renderSelectorCuenta("cuenta_liquidacion_compras", "Cuenta Liquidación de Compras", "Cuenta para liquidaciones de compras de bienes/servicios")}
              </Grid>
            </CardContent>
          </Card>

          {/* Margen de Utilidad */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                💰 Margen de Utilidad (PVP)
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="% Utilidad por defecto"
                    type="number"
                    value={asignacion.porcentaje_utilidad}
                    onChange={(e) => handleAsignacionChange("porcentaje_utilidad", parseFloat(e.target.value) || 0)}
                    InputProps={{ endAdornment: <Typography variant="body2">%</Typography> }}
                    inputProps={{ min: 0, max: 500, step: 1 }}
                    helperText="Se aplica sobre el costo de compra para sugerir el PVP"
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    <Typography variant="body2">
                      <strong>Ejemplo:</strong> Si el costo es $10.00 y el margen es {asignacion.porcentaje_utilidad}%, 
                      el PVP sugerido será <strong>${(10 * (1 + (asignacion.porcentaje_utilidad || 30) / 100)).toFixed(2)}</strong>. 
                      El usuario puede editarlo al registrar la compra.
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Módulos Habilitados */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                🔧 Módulos
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={asignacion.retenciones_habilitadas}
                    onChange={(e) => handleAsignacionChange("retenciones_habilitadas", e.target.checked)}
                  />
                }
                label="Habilitar módulo de Retenciones"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4 }}>
                Active esta opción si su empresa realiza retenciones en la fuente o de IVA al comprar.
              </Typography>

              <FormControlLabel
                sx={{ mt: 2 }}
                control={
                  <Switch
                    checked={asignacion.centro_costos_habilitado}
                    onChange={(e) => handleAsignacionChange("centro_costos_habilitado", e.target.checked)}
                  />
                }
                label="Habilitar Centro de Costos"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4 }}>
                Active esta opción si su empresa maneja centros de costos o departamentos.
              </Typography>

              <FormControlLabel
                sx={{ mt: 2 }}
                control={
                  <Switch
                    checked={asignacion.tesoreria_habilitada}
                    onChange={(e) => handleAsignacionChange("tesoreria_habilitada", e.target.checked)}
                  />
                }
                label="Habilitar Tesorería"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4 }}>
                Active esta opción para gestionar flujo de caja, conciliaciones bancarias y pagos.
              </Typography>

              <FormControlLabel
                sx={{ mt: 2 }}
                control={
                  <Switch
                    checked={asignacion.pos_habilitado}
                    onChange={(e) => handleAsignacionChange("pos_habilitado", e.target.checked)}
                  />
                }
                label="Habilitar Punto de Venta (POS)"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4 }}>
                Módulo de ventas rápidas tipo supermercado con búsqueda por código de barras y teclas rápidas.
              </Typography>

              <FormControlLabel
                sx={{ mt: 2 }}
                control={
                  <Switch
                    checked={asignacion.arqueo_caja_habilitado}
                    onChange={(e) => handleAsignacionChange("arqueo_caja_habilitado", e.target.checked)}
                  />
                }
                label="Habilitar Arqueo de Caja"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4 }}>
                Permite al cajero cerrar su turno, contar el efectivo y generar reporte de cuadre de caja.
              </Typography>

              <FormControlLabel
                sx={{ mt: 2 }}
                control={
                  <Switch
                    checked={asignacion.recibos_sin_factura_habilitado}
                    onChange={(e) => handleAsignacionChange("recibos_sin_factura_habilitado", e.target.checked)}
                  />
                }
                label="Habilitar Recibos sin Factura (Nota de Venta)"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4 }}>
                Permite emitir recibos de venta sin enviar al SRI. Luego se pueden convertir en factura electrónica.
              </Typography>
              {asignacion.recibos_sin_factura_habilitado && (
                <Box sx={{ ml: 4, mt: 1, mb: 1 }}>
                  <FormControl fullWidth size="small" sx={{ maxWidth: 400 }}>
                    <InputLabel>Cuenta contable para recibos pendientes</InputLabel>
                    <Select
                      value={asignacion.cuenta_recibos_pendientes || ''}
                      label="Cuenta contable para recibos pendientes"
                      onChange={(e) => handleAsignacionChange("cuenta_recibos_pendientes", e.target.value)}
                    >
                      <MenuItem value="">— Seleccionar cuenta —</MenuItem>
                      {(cuentas || []).filter(c => c.acepta_movimiento).map(c => (
                        <MenuItem key={c.id} value={c.id}>{c.codigo} - {c.nombre}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    Los recibos se registran en esta cuenta. Al facturar, se reversa y va a Ventas 15%/0%.
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>📦 Control de Inventario</Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={asignacion.permitir_venta_sin_stock}
                    onChange={(e) => handleAsignacionChange("permitir_venta_sin_stock", e.target.checked)}
                  />
                }
                label="Permitir venta sin stock (stock negativo)"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4 }}>
                <strong>Activado:</strong> Se puede facturar aunque no haya stock (queda en negativo hasta ingresar compra).
                <br /><strong>Desactivado:</strong> Bloquea la factura si no hay stock disponible.
              </Typography>
            </CardContent>
          </Card>

          {/* Botón Guardar */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={guardarAsignacion}
              disabled={saving}
              size="large"
            >
              {saving ? "Guardando..." : "Guardar Asignación de Cuentas"}
            </Button>
          </Box>
        </TabPanel>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
        />
      </Paper>
    </Box>
  );
}


// ============================================================
// Componente: PlanCuentasIntegrado (CRUD de cuentas contables)
// ============================================================
function PlanCuentasIntegrado({ cuentas, onCuentasChange, showSnackbar }) {
  const [cuentaEditando, setCuentaEditando] = useState(null);
  const [dialogNuevaCuenta, setDialogNuevaCuenta] = useState(false);
  const [formCuenta, setFormCuenta] = useState({
    codigo: "", nombre: "", tipo: "ACTIVO", acepta_movimiento: true, descripcion: ""
  });
  const [filtros, setFiltros] = useState({ tipo: "TODOS", busqueda: "", soloMovimiento: false });
  const [expandedNodes, setExpandedNodes] = useState(new Set(["1", "2", "3", "4", "5"]));

  const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  const getNombreGrupo = (codigo) => {
    const nombres = { "1": "ACTIVOS", "2": "PASIVOS", "3": "PATRIMONIO", "4": "INGRESOS", "5": "GASTOS" };
    return nombres[codigo] || "OTROS";
  };

  const getTipoGrupo = (codigo) => {
    const tipos = { "1": "ACTIVO", "2": "PASIVO", "3": "PATRIMONIO", "4": "INGRESO", "5": "GASTO" };
    return tipos[codigo] || "OTRO";
  };

  const cuentasFiltradas = cuentas.filter(cuenta => {
    const cumpleTipo = filtros.tipo === "TODOS" || cuenta.tipo === filtros.tipo;
    const cumpleBusqueda = !filtros.busqueda ||
      cuenta.codigo.toLowerCase().includes(filtros.busqueda.toLowerCase()) ||
      cuenta.nombre.toLowerCase().includes(filtros.busqueda.toLowerCase());
    const cumpleMovimiento = !filtros.soloMovimiento || cuenta.acepta_movimiento;
    return cumpleTipo && cumpleBusqueda && cumpleMovimiento;
  });

  const crearEstructuraJerarquica = (lista) => {
    const cuentasOrdenadas = [...lista].sort((a, b) =>
      a.codigo.localeCompare(b.codigo, undefined, { numeric: true })
    );
    const grupos = {};
    cuentasOrdenadas.forEach(cuenta => {
      const primerDigito = cuenta.codigo.charAt(0);
      if (!grupos[primerDigito]) {
        grupos[primerDigito] = {
          id: primerDigito, codigo: primerDigito,
          nombre: getNombreGrupo(primerDigito), tipo: getTipoGrupo(primerDigito),
          acepta_movimiento: false, hijos: []
        };
      }
      grupos[primerDigito].hijos.push(cuenta);
    });
    return Object.values(grupos);
  };

  const estructuraJerarquica = crearEstructuraJerarquica(cuentasFiltradas);

  const toggleExpansion = (codigo) => {
    const newExp = new Set(expandedNodes);
    newExp.has(codigo) ? newExp.delete(codigo) : newExp.add(codigo);
    setExpandedNodes(newExp);
  };

  const expandirTodo = () => setExpandedNodes(new Set(cuentas.map(c => c.codigo)));
  const contraerTodo = () => setExpandedNodes(new Set(["1", "2", "3", "4", "5"]));

  const handleEditarCuenta = (cuenta) => {
    setCuentaEditando(cuenta);
    setFormCuenta({
      codigo: cuenta.codigo, nombre: cuenta.nombre, tipo: cuenta.tipo,
      acepta_movimiento: cuenta.acepta_movimiento, descripcion: cuenta.descripcion || ""
    });
  };

  const handleGuardarCuenta = async () => {
    try {
      if (cuentaEditando) {
        await axios.put(`${API_URL}/api/contabilidad/plan-cuentas/${cuentaEditando.id}`, formCuenta, { headers: getAuthHeaders() });
        const actualizadas = cuentas.map(c => c.id === cuentaEditando.id ? { ...c, ...formCuenta } : c);
        onCuentasChange(actualizadas);
        showSnackbar("Cuenta actualizada", "success");
      } else {
        const response = await axios.post(`${API_URL}/api/contabilidad/plan-cuentas`, formCuenta, { headers: getAuthHeaders() });
        onCuentasChange([...cuentas, response.data.cuenta]);
        showSnackbar("Cuenta creada", "success");
      }
      setCuentaEditando(null);
      setDialogNuevaCuenta(false);
      setFormCuenta({ codigo: "", nombre: "", tipo: "ACTIVO", acepta_movimiento: true, descripcion: "" });
    } catch (error) {
      showSnackbar(error.response?.data?.error || "Error al guardar cuenta", "error");
    }
  };

  const handleEliminarCuenta = async (cuenta) => {
    if (!window.confirm(`¿Eliminar la cuenta "${cuenta.codigo} - ${cuenta.nombre}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/contabilidad/plan-cuentas/${cuenta.id}`, { headers: getAuthHeaders() });
      onCuentasChange(cuentas.filter(c => c.id !== cuenta.id));
      showSnackbar("Cuenta eliminada", "success");
    } catch (error) {
      showSnackbar(error.response?.data?.error || "Error al eliminar cuenta", "error");
    }
  };

  const inicializarPlanCuentas = async () => {
    const esReinicio = cuentas.length > 0;
    const msg = esReinicio
      ? "¿Reinicializar el plan de cuentas? Esto reemplazará TODAS las cuentas existentes."
      : "¿Inicializar el plan de cuentas NIIF Ecuador?";
    if (!window.confirm(msg)) return;

    try {
      await axios.post(`${API_URL}/api/contabilidad/plan-cuentas/inicializar`,
        { forzar_reinicializacion: true }, { headers: getAuthHeaders() });
      const cuentasRes = await axios.get(`${API_URL}/api/contabilidad/plan-cuentas`, { headers: getAuthHeaders() });
      onCuentasChange(cuentasRes.data);
      showSnackbar("Plan de cuentas inicializado correctamente", "success");
    } catch (error) {
      showSnackbar(error.response?.data?.error || "Error al inicializar", "error");
    }
  };

  const renderNodoArbol = (nodo, nivel = 0) => {
    const tieneHijos = nodo.hijos && nodo.hijos.length > 0;
    const estaExpandido = expandedNodes.has(nodo.codigo);
    const indentacion = nivel * 20;

    return (
      <React.Fragment key={nodo.codigo || nodo.id}>
        <TableRow hover sx={{ bgcolor: nivel === 0 ? "#f8f9fa" : "inherit" }}>
          <TableCell sx={{ pl: `${indentacion + 8}px` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {tieneHijos ? (
                <IconButton size="small" onClick={() => toggleExpansion(nodo.codigo)} sx={{ p: 0.5 }}>
                  {estaExpandido ? "▼" : "▶"}
                </IconButton>
              ) : <Box sx={{ width: 24 }} />}
              <Typography variant="body2" fontWeight={nivel < 2 ? 600 : 400}
                color={nivel === 0 ? "primary.main" : "inherit"}>
                {nodo.codigo}
              </Typography>
            </Box>
          </TableCell>
          <TableCell>
            <Typography variant="body2" fontWeight={nivel < 2 ? 600 : 400}
              color={nivel === 0 ? "primary.main" : "inherit"}>
              {nodo.nombre}
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Chip label={nodo.tipo} size="small" variant="outlined"
              color={
                nodo.tipo === "ACTIVO" ? "success" : nodo.tipo === "PASIVO" ? "error" :
                nodo.tipo === "PATRIMONIO" ? "info" : nodo.tipo === "INGRESO" ? "primary" :
                nodo.tipo === "GASTO" ? "secondary" : "default"
              } />
          </TableCell>
          <TableCell align="center">
            <Chip label={nodo.acepta_movimiento ? "Sí" : "No"} size="small"
              color={nodo.acepta_movimiento ? "success" : "default"} />
          </TableCell>
          <TableCell align="center">
            {nodo.id && (
              <>
                <IconButton size="small" onClick={() => handleEditarCuenta(nodo)} color="primary"><Edit /></IconButton>
                <IconButton size="small" onClick={() => handleEliminarCuenta(nodo)} color="error"><Delete /></IconButton>
              </>
            )}
          </TableCell>
        </TableRow>
        {tieneHijos && estaExpandido && nodo.hijos.map(hijo => renderNodoArbol(hijo, nivel + 1))}
      </React.Fragment>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" color="primary">
              📊 Plan de Cuentas Contables
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" startIcon={<Add />} onClick={() => setDialogNuevaCuenta(true)} size="small">
                Nueva Cuenta
              </Button>
              <Button variant="contained" startIcon={<Settings />} onClick={inicializarPlanCuentas} size="small"
                color={cuentas.length > 0 ? "warning" : "primary"}>
                {cuentas.length > 0 ? "Reinicializar Plan NIIF" : "Inicializar Plan NIIF"}
              </Button>
            </Box>
          </Box>

          {/* Filtros */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select value={filtros.tipo} label="Tipo"
                  onChange={(e) => setFiltros(prev => ({ ...prev, tipo: e.target.value }))}>
                  <MenuItem value="TODOS">Todos</MenuItem>
                  <MenuItem value="ACTIVO">Activos</MenuItem>
                  <MenuItem value="PASIVO">Pasivos</MenuItem>
                  <MenuItem value="PATRIMONIO">Patrimonio</MenuItem>
                  <MenuItem value="INGRESO">Ingresos</MenuItem>
                  <MenuItem value="GASTO">Gastos</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Buscar" value={filtros.busqueda}
                onChange={(e) => setFiltros(prev => ({ ...prev, busqueda: e.target.value }))}
                placeholder="Código o nombre..." />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={<Switch checked={filtros.soloMovimiento}
                  onChange={(e) => setFiltros(prev => ({ ...prev, soloMovimiento: e.target.checked }))} />}
                label="Solo movimiento" />
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button size="small" onClick={expandirTodo}>Expandir</Button>
                <Button size="small" onClick={contraerTodo}>Contraer</Button>
              </Box>
            </Grid>
          </Grid>
          <Typography variant="body2" color="text.secondary">
            Total: {cuentasFiltradas.length} cuentas
          </Typography>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell><strong>Código</strong></TableCell>
                  <TableCell><strong>Nombre</strong></TableCell>
                  <TableCell align="center"><strong>Tipo</strong></TableCell>
                  <TableCell align="center"><strong>Movimiento</strong></TableCell>
                  <TableCell align="center"><strong>Acciones</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {estructuraJerarquica.map(nodo => renderNodoArbol(nodo))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {cuentasFiltradas.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No se encontraron cuentas. {cuentas.length === 0 && "Inicialice el plan NIIF para comenzar."}
        </Alert>
      )}

      {/* Dialog crear/editar cuenta */}
      <Dialog open={cuentaEditando !== null || dialogNuevaCuenta}
        onClose={() => { setCuentaEditando(null); setDialogNuevaCuenta(false); }}
        maxWidth="sm" fullWidth>
        <DialogTitle>{cuentaEditando ? "Editar Cuenta" : "Nueva Cuenta"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Código *" value={formCuenta.codigo}
                onChange={(e) => setFormCuenta(prev => ({ ...prev, codigo: e.target.value }))}
                placeholder="Ej: 1.01.01.01" helperText="Use puntos para jerarquía" />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo *</InputLabel>
                <Select value={formCuenta.tipo} label="Tipo *"
                  onChange={(e) => setFormCuenta(prev => ({ ...prev, tipo: e.target.value }))}>
                  <MenuItem value="ACTIVO">Activo</MenuItem>
                  <MenuItem value="PASIVO">Pasivo</MenuItem>
                  <MenuItem value="PATRIMONIO">Patrimonio</MenuItem>
                  <MenuItem value="INGRESO">Ingreso</MenuItem>
                  <MenuItem value="GASTO">Gasto</MenuItem>
                  <MenuItem value="COSTO">Costo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Nombre *" value={formCuenta.nombre}
                onChange={(e) => setFormCuenta(prev => ({ ...prev, nombre: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Descripción" value={formCuenta.descripcion}
                onChange={(e) => setFormCuenta(prev => ({ ...prev, descripcion: e.target.value }))}
                multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={formCuenta.acepta_movimiento}
                  onChange={(e) => setFormCuenta(prev => ({ ...prev, acepta_movimiento: e.target.checked }))} />}
                label="Acepta movimientos contables" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCuentaEditando(null); setDialogNuevaCuenta(false); }}>Cancelar</Button>
          <Button onClick={handleGuardarCuenta} variant="contained"
            disabled={!formCuenta.codigo || !formCuenta.nombre}>
            {cuentaEditando ? "Actualizar" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
